import { ChangeDetectionStrategy, Component, computed, inject, input, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatChipListbox, MatChipListboxChange, MatChipOption } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { PushPipe } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas } from '@src/assets/css/fullscreen.template.scss';
import { environment } from '@src/environments/environment';
import { CustomMatIcon } from '@src/suite/common/enums/enums';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { SidecarEfffectActions, SidecarMenu } from '@src/suite/common/store/fullscreen';
import { selectCurrentRecordType } from '@src/suite/common/store/fullscreen/selectors/fullscreen.selectors';
import {
    selectSidecarCurrentRundownSelectedStory,
    selectSidecarCurrentStory,
    selectSidecarCurrentTopicSelectedAssignment,
    selectSidecarCurrentTopicSelectedStory,
    selectSidecarMenus,
    selectSidecarMenuSelected,
    selectSidecarSelectedAssignment,
    selectSidecarSelectedRundown,
    selectSidecarSelectedTopic,
} from '@src/suite/common/store/fullscreen/selectors/sidecar.selectors';
import { selectI18N, selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';

import { StDiscoverWireFullscreenComponent } from '../../../discover/components/wire-fullscreen/discover-wire-fullscreen/st-discover-wire-fullscreen';
import { AssignmentListSidecarDialogComponent } from '../../components/assignment-list-sidecar/assignment-list-sidecar';
import { BetaComponent } from '../../components/beta/st-beta.component';
import { NotesListSidecarDialogComponent } from '../../components/notes-list-sidecar/notes-list-sidecar.component';
import { StoriesListSidecarDialogComponent } from '../../components/stories-list-sidecar/stories-list-sidecar';
import { TopicListSidecarDialogComponent } from '../../components/topic-list-sidecar/topic-list-sidecar';
import { CardLeftBorderDirective } from '../../directives/card-left-border';
import { RundownListSidecarComponent } from '../rundown-list-sidecar/rundown-list-sidecar';
import { StMosFullScreenComponent } from '../st-mos-full-screen/st-mos-full-screen.component';
import { StRoverAIFullScreenComponent } from '../st-rover-ai-full-screen/st-rover-ai-full-screen.component';

@Component({
    selector: 'st-sidecar-full-screen',
    imports: [
        AssignmentListSidecarDialogComponent,
        BetaComponent,
        CardLeftBorderDirective,
        MatChipListbox,
        MatChipOption,
        MatIcon,
        MatIconButton,
        MatTooltip,
        NotesListSidecarDialogComponent,
        PushPipe,
        RundownListSidecarComponent,
        StDiscoverWireFullscreenComponent,
        StMosFullScreenComponent,
        StoriesListSidecarDialogComponent,
        StRoverAIFullScreenComponent,
        TopicListSidecarDialogComponent,
    ],
    templateUrl: './st-sidecar-full-screen.html',
    styleUrl: './st-sidecar-full-screen.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-sidecar-full-screen',
    },
})
export class SidecarComponent {
    private store = inject(Store);
    public readonly type = input<FullscreenLayoutAreas>();

    SideCartMenu = SidecarMenu;
    CustomMatIcon = CustomMatIcon;
    environment = environment;

    public recordType$ = this.store.select(selectCurrentRecordType);
    public currentSidecarMenu$ = this.store.select(selectCurrentRecordType);
    closeButtonLabel$ = this.store.select(selectTranslatedKey(I18nKeys.PSTWebCloseButtonLabel));
    sidecarMenus$ = this.store.select(selectSidecarMenus);
    readonly languageSettings = this.store.selectSignal(selectI18N)();

    readonly selectedSidecarMenu = computed(() => this.store.selectSignal(selectSidecarMenuSelected(this.type()))());
    readonly currentStory = computed(() => this.store.selectSignal(selectSidecarCurrentStory(this.type()))());
    readonly currentSelectedStory = computed(() => this.store.selectSignal(selectSidecarCurrentStory(this.type()))());

    readonly currentSelectedRundown = computed(() => this.store.selectSignal(selectSidecarSelectedRundown(this.type()))());
    readonly currentROSelectedStory = computed(() => this.store.selectSignal(selectSidecarCurrentRundownSelectedStory(this.type()))());

    readonly currentSelectedAssignment = computed(() => this.store.selectSignal(selectSidecarSelectedAssignment(this.type()))());

    readonly currentSelectedTopic = computed(() => this.store.selectSignal(selectSidecarSelectedTopic(this.type()))());
    readonly currentTopicSelectedStory = computed(() => this.store.selectSignal(selectSidecarCurrentTopicSelectedStory(this.type()))());
    readonly currentTopicSelectedAssignment = computed(() => this.store.selectSignal(selectSidecarCurrentTopicSelectedAssignment(this.type()))());

    sidecarMenuLabelsMap = {
        [SidecarMenu.ASSIGNMENTS]: I18nKeys.PSTWebAssignmentsLabel,
        [SidecarMenu.TOPICS]: I18nKeys.PSTWebTopicsLabel,
        [SidecarMenu.RUNDOWNS]: I18nKeys.PSTWebRundownsLabel,
        [SidecarMenu.STORIES]: I18nKeys.PSTWebStoriesLabel,
        [SidecarMenu.MOS]: I18nKeys.PSTWebPluginsLabel,
        [SidecarMenu.STORYPROPERTIES]: I18nKeys.PSTWebPropertiesLabel,
        [SidecarMenu.ROVER_AI]: I18nKeys.PSTWebRoverAiLabel,
        [SidecarMenu.NOTES]: I18nKeys.PSTWebNotesLabel,
        [SidecarMenu.DISCOVER_ITEMS]: I18nKeys.PSTWebDiscoverItemsLabel,
    };

    openSidecarMenu(event: MatChipListboxChange) {
        const selectMenu = () => {
            const menu = event?.source?.value as SidecarMenu;
            if (menu) {
                this.store.dispatch(SidecarEfffectActions.selectSidecarMenu({ menu, sidecarType: this.type() }));
            }
        };
        selectMenu();
    }

    backToList() {
        const backToList = () => {
            switch (this.selectedSidecarMenu()) {
                case SidecarMenu.TOPICS:
                    if (this.currentTopicSelectedAssignment()) {
                        this.store.dispatch(SidecarEfffectActions.setCurrentTopicSelectedAssignment({ guid: null, sidecarMenu: this.type() }));
                    } else if (this.currentTopicSelectedStory()) {
                        this.store.dispatch(SidecarEfffectActions.setCurrentTopicSelectedStory({ guid: null, sidecarMenu: this.type() }));
                    } else {
                        this.store.dispatch(SidecarEfffectActions.clearSelectedTopic({ sidecarType: this.type() }));
                    }
                    break;
                case SidecarMenu.ASSIGNMENTS:
                    this.store.dispatch(SidecarEfffectActions.clearSelectedAssignment({ sidecarType: this.type() }));
                    break;
                case SidecarMenu.RUNDOWNS:
                    if (this.currentROSelectedStory()) {
                        this.store.dispatch(SidecarEfffectActions.setCurrentROSelectedStory({ guid: null, sidecarMenu: this.type() }));
                    } else {
                        this.store.dispatch(SidecarEfffectActions.clearSelectedRundown({ sidecarType: this.type() }));
                    }
                    break;
                case SidecarMenu.STORIES:
                    this.store.dispatch(SidecarEfffectActions.clearSelectedStory({ sidecarType: this.type() }));
                    break;
                default:
                    this.store.dispatch(SidecarEfffectActions.clearSelectedAssignment({ sidecarType: this.type() }));
                    break;
            }
        };
        backToList();
    }

    readonly currentSelectedRecord = computed(() => {
        switch (this.selectedSidecarMenu()) {
            case SidecarMenu.TOPICS:
                return this.currentSelectedTopic();
            case SidecarMenu.ASSIGNMENTS:
                return this.currentSelectedAssignment();
            case SidecarMenu.RUNDOWNS:
                return this.currentROSelectedStory() ?? this.currentSelectedRundown();
            case SidecarMenu.STORIES:
                return this.currentSelectedStory();
        }
    });

    readonly isBackButtonShown = computed(() => {
        switch (this.selectedSidecarMenu()) {
            case SidecarMenu.TOPICS:
                if (this.currentSelectedRecord()?.Type === ObjectTypes.TOPIC) {
                    return true;
                }
                break;
            case SidecarMenu.ASSIGNMENTS:
                if (this.currentSelectedRecord()?.Type === ObjectTypes.ASSIGNMENT) {
                    return true;
                }
                break;
            case SidecarMenu.RUNDOWNS:
                if (this.currentSelectedRecord()?.Type === ObjectTypes.RUNDOWN || this.currentSelectedRecord()?.Type === ObjectTypes.SCRIPT) {
                    return true;
                }
                break;
            case SidecarMenu.STORIES:
                if (this.currentSelectedRecord()?.Type === ObjectTypes.SCRIPT) {
                    return true;
                }
                break;
            default:
                return false;
        }
    });
}
